import React, { useEffect, useState } from "react";

import Datatable from "../../../../core/common/dataTable/index";
import { ColumnsType } from "antd/es/table";
import { cellWidth } from "../../../../core/methods/tableCellWidth";
import {
  parseDate,
  safeLocaleCompare,
} from "../../../../core/methods/sorterMethods";
import { formattedDate } from "../../../../core/methods/formattedDate";
import useApi from "../../../../core/customhook/useApi";
import webservice_url from "../../../../core/webservice/Webservice_url";

import DynamicSingleSelect, {
  FilterErrors,
  FilterTypes,
  initialFilters,
} from "../../reusable/DynamicSingleSelect";

import Loading from "../../../../core/common/custom-loading/Loading";
import { PaginationType } from "../../../../types/common-type";

type Props = {
  status_id: string;
};

const ExtraCost: React.FC<Props> = ({ status_id }) => {
  const [allItemList, setAllItemList] = useState<any[]>([]);

  const [filterData, setFilterData] = useState<FilterTypes>(initialFilters);

  const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

  const [pagination, setPagination] = useState<PaginationType>({
    current: 1,
    pageSize: 1000,
    total: 0,
  });

  // ============================================================
  // API
  // ============================================================

  const { callApi: searchCall, loading } = useApi<any>(
    webservice_url.fetch_hr_pending_action_data,
    {
      method: "POST",
      autoCall: false,
      handleGlobalError: false,
    },
  );

  // ============================================================
  // FETCH DATA
  // ============================================================

  const fetchData = async (
    filters: FilterTypes = filterData,
    page: number = pagination.current,
    pageSize: number = pagination.pageSize,
  ) => {
    try {
      const payload = {
        status: status_id ?? "",

        page,
        limit: pageSize,

        // All filter values are arrays
        org_ids: filters?.organization ?? [],
        project_ids: filters?.project ?? [],
        location_ids: filters?.location ?? [],
        program_ids: filters?.program ?? [],
        department_ids: filters?.department ?? [],
      };

      console.log("Extra Cost Payload:", payload);

      const res = await searchCall(payload);

      if (res?.success) {
        setAllItemList(res?.data?.rows || []);

        setPagination((prev) => ({
          ...prev,
          total: res?.data?.pagination?.totalRecords || 0,
        }));
      } else {
        setAllItemList([]);
      }
    } catch (error: any) {
      console.error("Extra Cost API Error:", error?.response || error);

      setAllItemList([]);
    }
  };

  // ============================================================
  // FETCH WHEN PAGINATION CHANGES
  // ============================================================

  useEffect(() => {
    fetchData(filterData, pagination.current, pagination.pageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize]);

  // ============================================================
  // TABLE PAGINATION
  // ============================================================

  const handleTableChange = (pgConfig: any) => {
    if (
      pgConfig.current !== pagination.current ||
      pgConfig.pageSize !== pagination.pageSize
    ) {
      setPagination((prev) => ({
        ...prev,
        current: pgConfig.current,
        pageSize: pgConfig.pageSize,
      }));
    }
  };

  // ============================================================
  // HANDLE FILTER CHANGE
  // ============================================================

  const handleFilterChange = (name: keyof FilterTypes, value: string[]) => {
    setFilterData((prev) => {
      const updated: FilterTypes = {
        ...prev,
        [name]: value,
      };

      // Organization
      // => clear Project, Location, Program, Department
      if (name === "organization") {
        updated.project = [];
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      // Project
      // => clear Location, Program, Department
      if (name === "project") {
        updated.location = [];
        updated.program = [];
        updated.department = [];
      }

      // Location
      // => clear Program, Department
      if (name === "location") {
        updated.program = [];
        updated.department = [];
      }

      // Program
      // => clear Department
      if (name === "program") {
        updated.department = [];
      }

      return updated;
    });

    // Remove validation error when user selects value
    if (value.length > 0) {
      setFilterErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  // ============================================================
  // SEARCH
  // ============================================================

  const handleSearch = async () => {
    const errors: FilterErrors = {};

    // Organization mandatory
    if (!filterData.organization || filterData.organization.length === 0) {
      errors.organization = "Organization is required";
    }

    // Account / Project mandatory
    if (!filterData.project || filterData.project.length === 0) {
      errors.project = "Account is required";
    }

    setFilterErrors(errors);

    // Don't call API if validation fails
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Save filters
    localStorage.setItem(
      "admin-extra-cost-filters",
      JSON.stringify(filterData),
    );

    // Search should always start from page 1
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already on page 1
    await fetchData(filterData, 1, pagination.pageSize);
  };

  // ============================================================
  // CLEAR ALL
  // ============================================================

  const handleClearAll = async () => {
    const clearedFilters: FilterTypes = {
      organization: [],
      project: [],
      location: [],
      program: [],
      department: [],
    };

    setFilterData(clearedFilters);
    setFilterErrors({});

    localStorage.removeItem("admin-extra-cost-filters");

    // Reset pagination
    if (pagination.current !== 1) {
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));

      return;
    }

    // Already page 1
    await fetchData(clearedFilters, 1, pagination.pageSize);
  };

  // ============================================================
  // TABLE COLUMNS
  // ============================================================

  const columns: ColumnsType<any> = [
    {
      title: "Applicant Name",
      dataIndex: "applicant_name",
      onCell: () => cellWidth(200),
      render: (text) => <div className="btn-link text-capitalize">{text}</div>,
      sorter: (a, b) => safeLocaleCompare(a, b, "applicant_name"),
    },

    {
      title: "SBU Name",
      dataIndex: "sbu_name",
      onCell: () => cellWidth(140),
      sorter: (a, b) => safeLocaleCompare(a, b, "sbu_name"),
    },

    {
      title: "Case Id",
      dataIndex: "case_id",
      onCell: () => cellWidth(110),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_id"),
    },

    {
      title: "Check Name",
      dataIndex: "check_name",
      onCell: () => cellWidth(200),
      sorter: (a, b) => safeLocaleCompare(a, b, "check_name"),
    },

    {
      title: "Key Value",
      dataIndex: "key_value",
      render: (text: string) => <div style={{ width: 200 }}>{text}</div>,
      sorter: (a, b) => safeLocaleCompare(a, b, "key_value"),
    },

    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a, b) =>
        parseFloat(String(a.amount ?? "").replace(/[^\d.]/g, "")) -
        parseFloat(String(b.amount ?? "").replace(/[^\d.]/g, "")),
    },

    {
      title: "Approval Status",
      dataIndex: "approval_status",
      sorter: (a, b) => safeLocaleCompare(a, b, "approval_status"),
    },

    {
      title: "Created At",
      dataIndex: "created_at",
      render: (text: string) => {
        const date = formattedDate(text) ?? "-";

        return <span>{date}</span>;
      },
      sorter: (a: any, b: any) =>
        parseDate(a.created_at).getTime() - parseDate(b.created_at).getTime(),
    },

    {
      title: "Case Spoc",
      dataIndex: "case_spoc",
      onCell: () => cellWidth(130),
      sorter: (a, b) => safeLocaleCompare(a, b, "case_spoc"),
    },

    {
      title: "Email Id",
      dataIndex: "email_id",
      sorter: (a, b) => safeLocaleCompare(a, b, "email_id"),
    },

    {
      title: "Payment Link",
      dataIndex: "payment_link",
      onCell: () => cellWidth(150),
      sorter: (a, b) => safeLocaleCompare(a, b, "payment_link"),
    },

    {
      title: "Payment Status",
      dataIndex: "payment_status",
      sorter: (a, b) => safeLocaleCompare(a, b, "payment_status"),
    },
  ];

  // ============================================================
  // JSX
  // ============================================================

  return (
    <div className="px-2">
      <DynamicSingleSelect
        fields={[
          "organization",
          "project",
          "location",
          "program",
          "department",
        ]}
        filterData={filterData}
        onFilterDataChange={(name, value, _labels) => {
          handleFilterChange(name, value);
        }}
        parentError={filterErrors}
        showButton={true}
        buttonText="Search"
        onButtonClick={handleSearch}
        disableButton={loading}
        onClearAll={handleClearAll}
      />

      {/* Table */}
      <div className="card mt-3">
        {loading ? (
          <Loading />
        ) : (
          <Datatable
            rowKey="case_id"
            dataSource={allItemList}
            columns={columns}
            fixedHeader
            tableHeight={650}
            Selection={false}
            pagination={pagination}
            handleTableChange={handleTableChange}
          />
        )}
      </div>
    </div>
  );
};

export default ExtraCost;
